import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { ListView } from '../../design-system/list-view/ListView'
import type { ActiveFilterChip } from '../../design-system/list-view/FilterPanel'
import type { Density, ExportFormat, SortSpec } from '../../design-system/list-view/types'
import { usePersistentState } from '../../lib/usePersistentState'
import { useDebouncedValue } from '../../lib/useDebouncedValue'
import { useSavedViews } from '../../lib/savedViews'
import { rsqlAnd, rsqlContains } from '../../lib/rsql'
import { runExport, exportCapNote, type ExportColumn } from '../../lib/export'
import { listProgramFilings } from '../../api/insights'
import { ApiError } from '../../api/problem'
import type { ProgramFilingListView } from '../../types/programFiling'
import { PROGRAM_FILING_COLUMN_ORDER, buildProgramFilingColumns } from './columns'

const PAGE_LIMIT = 50

function buildSort(sort: SortSpec[]): string | null {
  if (sort.length === 0) return null
  return sort.map((s) => (s.direction === 'desc' ? `-${s.columnId}` : s.columnId)).join(',')
}

/** Everything a saved view needs to capture for this list (Saved Views/Filters story). The
 * dashboard drill-through `filter` query param is deliberately excluded — it identifies *how the
 * user arrived*, not a reusable saved filter. */
interface FilingsSavedViewState {
  search: string
  sort: SortSpec[]
  columnOrder: string[]
  hiddenColumnIds: string[]
  pinnedColumnIds: string[]
  density: Density
}

/**
 * AC12 drill target — a minimal, read-only cross-case filing list (design
 * note §1.5.2) reusing every universal list-view control. Arrives pre-
 * filtered from the dashboard's `filter` query param (an RSQL clause built
 * by `screens/insights/drill.ts`); the reference `ListView` shell means this
 * screen also stands on its own as the eventual M22 Cases module's filing
 * tab foundation.
 */
export function FilingsListScreen() {
  const [searchParams, setSearchParams] = useSearchParams()
  const drillFilter = searchParams.get('filter')

  /**
   * IB-90 defect 2 fix — strips the drill `filter` query param (leaving any
   * other params untouched) and lets the `filterString`/`fetchPage` effect
   * below re-query the (tenant/visibility-scoped) list without it.
   */
  const clearDrillFilter = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.delete('filter')
        return next
      },
      { replace: true },
    )
  }, [setSearchParams])

  const columns = useMemo(() => buildProgramFilingColumns(), [])
  const [columnOrder, setColumnOrder] = usePersistentState<string[]>('iw.filings.columnOrder', PROGRAM_FILING_COLUMN_ORDER)
  const [hiddenColumnIdsArr, setHiddenColumnIdsArr] = usePersistentState<string[]>(
    'iw.filings.hiddenColumns',
    columns.filter((c) => c.defaultHidden).map((c) => c.id),
  )
  const [pinnedColumnIdsArr, setPinnedColumnIdsArr] = usePersistentState<string[]>('iw.filings.pinnedColumns', ['accountName'])
  const [density, setDensity] = usePersistentState<Density>('iw.filings.density', 'compact')

  const hiddenColumnIds = useMemo(() => new Set(hiddenColumnIdsArr), [hiddenColumnIdsArr])
  const pinnedColumnIds = useMemo(() => new Set(pinnedColumnIdsArr), [pinnedColumnIdsArr])

  const [search, setSearch] = useState('')
  const debouncedSearch = useDebouncedValue(search, 250)
  const [sort, setSort] = useState<SortSpec[]>([{ columnId: 'programName', direction: 'asc' }])
  const [selection, setSelection] = useState<Set<string>>(new Set())

  const [filings, setFilings] = useState<ProgramFilingListView[]>([])
  const [cursor, setCursor] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const filterString = useMemo(
    () => rsqlAnd(drillFilter || null, rsqlContains('programName', debouncedSearch) || null) || null,
    [drillFilter, debouncedSearch],
  )
  const sortString = useMemo(() => buildSort(sort), [sort])
  const latestRequestRef = useRef(0)

  const fetchPage = useCallback(
    async (afterCursor: string | null, replace: boolean) => {
      const requestId = ++latestRequestRef.current
      try {
        if (replace) {
          setLoading(true)
          setError(null)
        } else {
          setLoadingMore(true)
        }
        const page = await listProgramFilings({ filter: filterString, sort: sortString, cursor: afterCursor, limit: PAGE_LIMIT })
        if (latestRequestRef.current !== requestId) return
        setFilings((prev) => (replace ? page.items : [...prev, ...page.items]))
        setCursor(page.nextCursor)
      } catch (err) {
        if (latestRequestRef.current !== requestId) return
        setError(
          err instanceof ApiError
            ? (err.problem.detail ?? err.message)
            : 'Could not load filings. Check your connection and try again.',
        )
      } finally {
        if (latestRequestRef.current === requestId) {
          setLoading(false)
          setLoadingMore(false)
        }
      }
    },
    [filterString, sortString],
  )

  useEffect(() => {
    setSelection(new Set())
    void fetchPage(null, true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterString, sortString])

  function retry() {
    void fetchPage(null, true)
  }

  const activeFilterChips: ActiveFilterChip[] = useMemo(
    () => (drillFilter ? [{ id: 'drill', label: `From dashboard: ${drillFilter}` }] : []),
    [drillFilter],
  )

  function toggleColumnHidden(id: string) {
    setHiddenColumnIdsArr((prev) => (prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]))
  }
  function moveColumn(id: string, direction: -1 | 1) {
    setColumnOrder((prev) => {
      const index = prev.indexOf(id)
      const target = index + direction
      if (index === -1 || target < 0 || target >= prev.length) return prev
      const next = [...prev]
      const tmp = next[index]!
      next[index] = next[target]!
      next[target] = tmp
      return next
    })
  }
  function toggleColumnPin(id: string) {
    setPinnedColumnIdsArr((prev) => (prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]))
  }

  const savedViewState: FilingsSavedViewState = useMemo(
    () => ({ search, sort, columnOrder, hiddenColumnIds: hiddenColumnIdsArr, pinnedColumnIds: pinnedColumnIdsArr, density }),
    [search, sort, columnOrder, hiddenColumnIdsArr, pinnedColumnIdsArr, density],
  )
  const applySavedViewState = useCallback((state: FilingsSavedViewState) => {
    setSearch(state.search)
    setSort(state.sort)
    setColumnOrder(state.columnOrder)
    setHiddenColumnIdsArr(state.hiddenColumnIds)
    setPinnedColumnIdsArr(state.pinnedColumnIds)
    setDensity(state.density)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const savedViews = useSavedViews<FilingsSavedViewState>('filings', savedViewState, applySavedViewState)

  const exportColumns: ExportColumn<ProgramFilingListView>[] = useMemo(
    () =>
      columnOrder
        .map((id) => columns.find((c) => c.id === id))
        .filter((c): c is NonNullable<typeof c> => c !== undefined && !hiddenColumnIds.has(c.id))
        .map((c) => ({ id: c.id, header: c.header, toText: c.toExportText })),
    [columnOrder, hiddenColumnIds, columns],
  )

  function handleExport(format: ExportFormat) {
    runExport(format, 'Filings', filings, exportColumns)
  }
  function handleExportSelected(selectedIds: string[]) {
    const idSet = new Set(selectedIds)
    runExport('csv', 'Filings (selected)', filings.filter((f) => idSet.has(f.id)), exportColumns)
  }

  const state: 'loading' | 'error' | 'empty-tenant' | 'empty-filtered' | 'ready' = error
    ? 'error'
    : loading
      ? 'loading'
      : filings.length === 0
        ? drillFilter || debouncedSearch.trim() !== ''
          ? 'empty-filtered'
          : 'empty-tenant'
        : 'ready'

  return (
    <ListView<ProgramFilingListView>
      entityIcon="briefcase"
      title="Filings"
      countLabel={loading ? undefined : `${filings.length}${cursor ? '+' : ''}`}
      quickFilters={[]}
      activeQuickFilterIds={new Set()}
      onQuickFilterToggle={() => {}}
      search={search}
      onSearchChange={setSearch}
      searchLabel="Search filings"
      searchPlaceholder="Search by program…"
      filterChildren={<p style={{ fontSize: 'var(--iw-size-sm)', color: 'var(--iw-text-muted)' }}>Filtered by the dashboard drill-through.</p>}
      activeFilterCount={activeFilterChips.length}
      activeFilterChips={activeFilterChips}
      onRemoveFilterChip={(id) => {
        if (id === 'drill') clearDrillFilter()
      }}
      onClearFilters={() => {
        setSearch('')
        clearDrillFilter()
      }}
      savedViews={savedViews}
      onExport={handleExport}
      exportNote={exportCapNote(filings.length, Boolean(cursor))}
      columns={columns}
      columnOrder={columnOrder}
      hiddenColumnIds={hiddenColumnIds}
      pinnedColumnIds={pinnedColumnIds}
      onToggleColumnHidden={toggleColumnHidden}
      onMoveColumn={moveColumn}
      onToggleColumnPin={toggleColumnPin}
      sort={sort}
      onSortChange={setSort}
      density={density}
      onDensityChange={setDensity}
      state={state}
      rows={filings}
      getRowId={(row) => row.id}
      selection={selection}
      onSelectionChange={setSelection}
      bulkActions={[{ id: 'export-selected', label: 'Export selected', icon: 'download', onRun: handleExportSelected }]}
      errorMessage={error ?? undefined}
      onRetry={retry}
      emptyTenant={{
        icon: 'briefcase',
        title: 'No filings match this view',
        description: 'Every cross-case filing list carries Account, Legal entity, Case and Fiscal year.',
      }}
      hasMore={Boolean(cursor)}
      onLoadMore={() => void fetchPage(cursor, false)}
      loadingMore={loadingMore}
    />
  )
}
